"use client";

import { datadogRum } from "@datadog/browser-rum";
import type { RumEvent } from "@datadog/browser-rum";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { isIgnoredRumError } from "./ignored-errors";
import { useAnonymousId, type RumUserData } from "./user-context";

// Query params that carry PII and must not land in RUM. Partner checkout
// links embed the buyer's email (?email=), and the client strips it from the
// address bar on mount — but the first view/resource events are captured
// before that, so redact here too. Builder-partner landing links carry the
// same email as ?builderPartnerEmail=.
const SENSITIVE_QUERY_PARAMS = ["email", "builderPartnerEmail"];

/** Replaces sensitive query-param values with "redacted" in any URL field. */
export function redactUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    let changed = false;
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.set(param, "redacted");
        changed = true;
      }
    }
    return changed ? url.toString() : rawUrl;
  } catch {
    return rawUrl;
  }
}

/**
 * Strips sensitive query params from every URL an event carries. Mutates in
 * place because that is the contract `beforeSend` expects. Every RUM event
 * type carries `view`; only resource events carry `resource`.
 */
export function redactSensitiveQueryParams(event: RumEvent): void {
  event.view.url = redactUrl(event.view.url);
  if (event.view.referrer) {
    event.view.referrer = redactUrl(event.view.referrer);
  }
  if (event.type === "resource") {
    event.resource.url = redactUrl(event.resource.url);
  }
}

/**
 * The deploy environment, from the hostname. NODE_ENV is "production" in every
 * Next build, so it reported staging as production. The staging hosts are all
 * `staging.<app>.yardzen.com` or `<app>.staging.yardzen.com`.
 */
export function rumEnvForHostname(hostname: string): string {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "development";
  }
  return /(^|\.)staging\./.test(hostname) ? "staging" : "production";
}

const DatadogCore: FC<{
  service: string;
  treatments?: Record<string, string>;
  /** See {@link RumUserData}; omit when the app does not track auth. */
  userData?: RumUserData;
  sessionReplaySampleRate?: number;
}> = ({ service, treatments, userData, sessionReplaySampleRate = 10 }) => {
  const version = process.env.NEXT_PUBLIC_COMMIT_SHA || "development";
  const initRef = useRef(false);
  const lastTreatmentsRef = useRef<string>("");
  const anonymousId = useAnonymousId();

  useEffect(() => {
    try {
      // Only initialize once per session
      if (initRef.current) {
        // If already initialized, only update treatments if they changed
        if (treatments) {
          const treatmentsString = JSON.stringify(treatments);
          if (treatmentsString !== lastTreatmentsRef.current) {
            console.log(
              "[Datadog] Updating feature flag evaluations:",
              treatments,
            );
            lastTreatmentsRef.current = treatmentsString;
            Object.entries(treatments).forEach(([feature, treatment]) => {
              datadogRum.addFeatureFlagEvaluation(feature, treatment);
            });
          }
        }
        return;
      }

      console.log("[Datadog] Version:", version);
      console.log(
        "[Datadog] Session Replay Sample Rate:",
        sessionReplaySampleRate,
      );

      // Initialize Datadog RUM only once
      if (!datadogRum.getInternalContext()) {
        initRef.current = true;
        datadogRum.init({
          applicationId: "85dc5232-2f23-40db-96b4-e2ff39c2df6f",
          clientToken: "pub3a41b898b58be888681bd1763723c3a6",
          site: "us5.datadoghq.com",
          service,
          env: rumEnvForHostname(window.location.hostname),
          version,
          sessionSampleRate: 100,
          sessionReplaySampleRate,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: "allow",
          enableExperimentalFeatures: ["feature_flags"],
          allowedTracingUrls: [
            // Match all *.yardzen.com origins except the GTM server-side
            // proxy (yardwork.yardzen.com) — injecting Datadog trace headers
            // into GA collect requests causes CORS preflight failures.
            (url: string) =>
              /^https:\/\/[^/]+\.yardzen\.com/.test(url) &&
              !url.startsWith("https://yardwork.yardzen.com"),
            // Include localhost to allow trace header testing in browser DevTools
            // even when APM agent is not running locally. Minimal overhead.
            /^http:\/\/localhost/,
          ],
          beforeSend: (event: RumEvent) => {
            if (event.type === "error" && isIgnoredRumError(event.error)) {
              return false;
            }
            redactSensitiveQueryParams(event);
            return true;
          },
        });

        datadogRum.startSessionReplayRecording();
        console.log(
          "[Datadog] RUM initialized successfully with version:",
          version,
        );

        // Set initial treatments
        if (treatments) {
          lastTreatmentsRef.current = JSON.stringify(treatments);
          console.log(
            "[Datadog] Setting feature flag evaluations:",
            treatments,
          );
          Object.entries(treatments).forEach(([feature, treatment]) => {
            datadogRum.addFeatureFlagEvaluation(feature, treatment);
          });
        }
      }
    } catch (error) {
      console.error("[Datadog] Failed to initialize:", error);
    }
  }, [service, treatments, version, sessionReplaySampleRate]);

  // Keep the yzsa id on every event so pre- and post-login sessions link up.
  useEffect(() => {
    if (!datadogRum.getInternalContext() || !anonymousId) return;
    datadogRum.setGlobalContextProperty("anonymousId", anonymousId);
  }, [anonymousId]);

  // Identity goes in the reserved `usr` attributes (setUser), which drive the
  // User column, "users affected" and per-user monitors. `is_authenticated`
  // matches what the WordPress snippet sends: it calls setUser with the
  // anonymous visitor id, so `@usr.id` alone does not mean logged in.
  const userId = userData?.userId;
  const email = userData?.email;
  const knowsAuth = userData !== undefined;
  useEffect(() => {
    if (!datadogRum.getInternalContext() || !knowsAuth) return;

    try {
      if (userId) {
        datadogRum.setUser({ id: userId, email });
        datadogRum.setGlobalContextProperty("is_authenticated", true);
      } else {
        datadogRum.clearUser();
        datadogRum.setGlobalContextProperty("is_authenticated", false);
      }
    } catch (error) {
      console.error("[Datadog] Failed to set user:", error);
    }
  }, [knowsAuth, userId, email]);

  return null;
};

export const Datadog = DatadogCore;
export * from "./rum-events";
